import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  IRichTextInputProps,
  RichTextButton,
  RichTextInput,
  StyledMenuBarWrapper,
  StyledRteEditor,
} from '@framework/form';
import { Button, Popconfirm, Space, Tooltip } from 'antd';
import clx from 'classnames';
import React, { useEffect, useMemo } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { ExpressionsEditorStyles } from '../../schema/expressions/compute/input/ComputeExpressionMinimalBaseInput';
import { ComputeExpressionReferenceNode } from '../../schema/expressions/compute/input/nodes/ComputeExpressionReferenceNode';
import { computeExpressionReferenceSuggestion } from '../../schema/expressions/compute/input/reference-suggestions/computeExpressionReferenceSuggestion';
import { useTreeReferencesContext } from '../../tree/references/TreeReferencesContext';
import { ConditionsEditorInput } from '../expressions-editor/conditions/ConditionsEditorInput';
import { IAdvancedRichText, IAdvancedRichTextItem } from './IAdvancedRichText';
import { RichTextAdvancedExtensions } from './RichTextAdvancedExtensions';
import { RichTextFooterRow } from './RichTextFooterRow';
import { RichTextInfoInputButton } from './RichTextInfoInputButton';
import { RichTextItemFooterDisplay } from './item-footer/RichTextItemFooterDisplay';
import { ReferenceRichTextButton } from './menu-bar/ReferenceRichTextButton';

const RichTextAdvancedInputStyles = styled.div`
  .rte-container {
    &:not(.first-item) {
      ${StyledMenuBarWrapper} {
        &.rte-menu-wrapper-lg,
        .rte-menu-wrapper-sm {
          border-top-color: transparent;
          border-radius: 0;
        }
      }
    }
    &:not(.last-item) {
      ${StyledRteEditor} {
        &.rte-input-lg,
        .rte-input-sm {
          border-radius: 0;
        }
      }
    }
    ${StyledRteEditor} {
      &.rte-input-lg,
      .rte-input-sm {
        border-bottom-right-radius: 0;
      }
    }
  }
`;

const AddButtonStyles = styled(Button)`
  &&& {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    margin-top: -1px;
  }
`;

export interface IRichTextAdvancedInputProps extends IRichTextInputProps {}

export function RichTextAdvancedInput(props: IRichTextAdvancedInputProps) {
  const { name } = props;
  const { references } = useTreeReferencesContext();

  // TODO Trovare un modo più ergonomico di descrivere la tipizzazione di react-hook-form
  const methods = useFormContext();
  methods.register(name);
  const { fields, append, remove, replace } = useFieldArray<
    Record<string, IAdvancedRichTextItem[]>,
    string
  >({ name });

  // Controlliamo se il campo era una stringa precedentemente e la aggiorniamo
  // in automatico con il nuovo formato RTE (array di oggetti)
  const watchedFields: IAdvancedRichText | string = useWatch({
    name,
    defaultValue: fields,
  });

  useEffect(() => {
    if (typeof watchedFields === 'string') {
      console.log(`Autoaggiorno il campo RTE ${name} con il seguente testo HTML:`, watchedFields); // prettier-ignore
      replace([{ text: watchedFields, type: 'text' }]);
      return;
    }

    // Aggiunge il primo blocco di testo automaticamente
    if (!watchedFields || watchedFields.length == 0) {
      replace([{ text: '', type: 'text' }]);
      return;
    }
  }, [watchedFields, replace, name]);

  const extensions = useMemo(() => {
    const suggestions = computeExpressionReferenceSuggestion(references ?? []);
    return [
      ...RichTextAdvancedExtensions.filter(
        extension => extension.name !== 'compute-expression-reference',
      ),
      // Aggiungo l'estensione per il suggerimento
      ComputeExpressionReferenceNode.configure({
        suggestion: suggestions,
      }),
    ];
  }, [references]);

  return (
    <RichTextAdvancedInputStyles className="RichTextAdvancedInputStyles">
      <Space size={0} direction="vertical" style={{ width: '100%' }}>
        {fields?.map((field, i) => {
          const fieldPrefix = `${props.name}[${i}]`;

          return (
            <div
              key={field.id}
              className={clx('rte-container', {
                'first-item': i === 0,
                'last-item': i === fields.length - 1,
              })}
            >
              <ExpressionsEditorStyles>
                <RichTextInput
                  {...props}
                  extensions={extensions}
                  extendedMenu={editor => (
                    <ReferenceRichTextButton
                      editor={editor}
                      references={references}
                    />
                  )}
                  name={`${fieldPrefix}.text`}
                  rteItemTypeFieldName={`${fieldPrefix}.type`}
                  rows={2}
                  extra={
                    <Space>
                      {props.extra}
                      <RichTextInfoInputButton name={`${fieldPrefix}.type`} />
                      <Tooltip
                        title="Imposta regole di visibilità"
                        placement="left"
                      >
                        <ConditionsEditorInput
                          size="small"
                          name={`${fieldPrefix}.rule`}
                          references={references}
                        />
                      </Tooltip>
                      <Popconfirm
                        placement="topRight"
                        title="Vuoi eliminare il testo?"
                        okText={
                          <span>
                            <DeleteOutlined /> Elimina
                          </span>
                        }
                        description="Verranno eliminate anche le regole di visibilità associate."
                        okButtonProps={{
                          danger: true,
                          type: 'primary',
                          disabled: fields.length < 2,
                        }}
                        onConfirm={() => {
                          remove(i);
                        }}
                      >
                        <RichTextButton
                          icon={<DeleteOutlined />}
                          title="Elimina testo"
                          danger
                        />
                      </Popconfirm>
                    </Space>
                  }
                  footer={<RichTextItemFooterDisplay name={`${fieldPrefix}`} />}
                />
              </ExpressionsEditorStyles>
            </div>
          );
        })}
        <RichTextFooterRow>
          <Space>
            <AddButtonStyles
              icon={<PlusOutlined aria-hidden="true" />}
              onClick={() => {
                append({
                  type: 'text',
                  text: '',
                });
              }}
              title="Aggiungi Testo"
              children={<span>Aggiungi Testo</span>}
            />
          </Space>
        </RichTextFooterRow>
      </Space>
    </RichTextAdvancedInputStyles>
  );
}
